import { useState, useEffect, useRef } from "react";
import { ShoppingBag, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getShopifyCollectionUrl, SHOPIFY_DOMAIN } from "@/lib/shopify";
import NavMobileDrawer, { SHOP_LINKS } from "@/components/NavMobileDrawer";

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Hover-intent for the dropdown — opens instantly, closes with a small delay
  const openShop = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setShopOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setShopOpen(false), 180);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-stone-950/95 backdrop-blur-md border-b border-stone-800/60"
          : "bg-stone-950/85 backdrop-blur-sm"
      }`}
      data-testid="navigation"
    >
      <div className="container-luxury">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <a
            href="/"
            className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#F4EFE6] hover:text-stone-300 transition-colors"
            data-testid="nav-logo"
          >
            UNIXORN
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-9">
            <a
              href="/"
              className="text-sm font-medium tracking-wide text-[#F4EFE6] hover:text-stone-300 transition-colors uppercase"
              data-testid="nav-home"
            >
              Home
            </a>

            {/* SHOP — dropdown */}
            <div
              className="relative"
              onMouseEnter={openShop}
              onMouseLeave={scheduleClose}
            >
              <button
                className="text-sm font-medium tracking-wide text-[#F4EFE6] hover:text-stone-300 transition-colors uppercase flex items-center gap-1.5"
                aria-haspopup="true"
                aria-expanded={shopOpen}
                onClick={() => (window.location.href = getShopifyCollectionUrl("all"))}
                data-testid="nav-shop"
              >
                Shop
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${shopOpen ? "rotate-180" : ""}`} />
              </button>

              {shopOpen && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                  onMouseEnter={openShop}
                  onMouseLeave={scheduleClose}
                >
                  <div className="min-w-[200px] bg-stone-950/97 backdrop-blur-md border border-stone-800/60 shadow-2xl py-2">
                    {SHOP_LINKS.map((l) => (
                      <a
                        key={l.key}
                        href={getShopifyCollectionUrl(l.key)}
                        className="block px-5 py-2.5 text-[11px] tracking-[0.3em] uppercase text-stone-300 hover:text-[#F4EFE6] hover:bg-stone-900/60 transition-colors"
                        data-testid={`nav-shop-${l.key}`}
                      >
                        {l.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="/footwear"
              className="text-sm font-medium tracking-wide text-[#F4EFE6] hover:text-stone-300 transition-colors uppercase"
              data-testid="nav-footwear"
            >
              Footwear
            </a>
            <a
              href="/chronicles"
              className="text-sm font-medium tracking-wide text-[#F4EFE6] hover:text-stone-300 transition-colors uppercase"
              data-testid="nav-chronicles"
            >
              Archive
            </a>
            <a
              href="/about"
              className="text-sm font-medium tracking-wide text-[#F4EFE6] hover:text-stone-300 transition-colors uppercase"
              data-testid="nav-about"
            >
              About
            </a>
          </div>

          {/* Right side: mobile menu + cart */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden hover:bg-transparent hover:text-stone-300 text-[#F4EFE6]"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              <span className="block w-5">
                <span className={`block h-0.5 bg-current transition-transform ${mobileOpen ? "translate-y-1.5 rotate-45" : ""}`} />
                <span className={`block h-0.5 bg-current mt-1.5 transition-opacity ${mobileOpen ? "opacity-0" : ""}`} />
                <span className={`block h-0.5 bg-current mt-1.5 transition-transform ${mobileOpen ? "-translate-y-1.5 -rotate-45" : ""}`} />
              </span>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => (window.location.href = SHOPIFY_DOMAIN)}
              className="hover:bg-transparent hover:text-stone-300 transition-colors relative text-[#F4EFE6]"
              aria-label="Shop"
              data-testid="nav-cart"
            >
              <ShoppingBag className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && <NavMobileDrawer onClose={() => setMobileOpen(false)} />}
      </div>
    </nav>
  );
};

export default Navigation;
